import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Save, Loader2 } from 'lucide-react';
import axios from 'axios';
import type { Rate, TranslateFn } from '../../types';

const API_URL = process.env.REACT_APP_BACKEND_URL;

interface RatesTabProps {
  token: string;
  t: TranslateFn;
}

export const RatesTab = ({ token, t }: RatesTabProps) => {
  const [rates, setRates] = useState<Rate[]>([]);
  const [editedPrices, setEditedPrices] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  const authHeaders = { Authorization: `Bearer ${token}` };

  const fetchRates = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/rates`, { headers: { Authorization: `Bearer ${token}` } });
      setRates(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const grouped: Record<string, Rate[]> = rates.reduce((acc: Record<string, Rate[]>, rate) => {
    if (!acc[rate.service_type]) acc[rate.service_type] = [];
    acc[rate.service_type].push(rate);
    return acc;
  }, {});

  const handlePriceChange = (rateId: string, value: string) => {
    setEditedPrices(prev => ({ ...prev, [rateId]: value }));
  };

  const handleSave = async (rateId) => {
    const price = parseFloat(editedPrices[rateId]);
    if (isNaN(price) || price < 0) return;
    setSaving(prev => ({ ...prev, [rateId]: true }));
    try {
      await axios.put(`${API_URL}/api/admin/rates/${rateId}`, { price_thb: price }, { headers: authHeaders });
      setEditedPrices(prev => { const n = { ...prev }; delete n[rateId]; return n; });
      fetchRates();
    } catch {
      alert('Failed to update price');
    } finally {
      setSaving(prev => ({ ...prev, [rateId]: false }));
    }
  };

  if (loading) return <div className="flex justify-center p-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="rates-management">
      <p className="text-sm text-muted-foreground">{t('admin.ratesDescription') || 'Edit prices below and click Save to update.'}</p>
      {Object.entries(grouped).map(([serviceType, serviceRates]) => (
        <Card key={serviceType}>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{serviceType}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {serviceRates.map(rate => {
                const isEdited = editedPrices[rate.id] !== undefined;
                const currentPrice = isEdited ? editedPrices[rate.id] : rate.price_thb;
                return (
                  <div key={rate.id} className="flex items-center gap-3" data-testid={`rate-row-${rate.id}`}>
                    <Badge variant="outline" className="min-w-[70px] justify-center">{rate.duration_minutes} min</Badge>
                    <div className="flex items-center gap-1 flex-1 max-w-[180px]">
                      <span className="text-sm font-medium">฿</span>
                      <Input
                        type="number"
                        value={currentPrice}
                        onChange={(e) => handlePriceChange(rate.id, e.target.value)}
                        className="h-9"
                        min="0"
                        step="50"
                        data-testid={`rate-price-${rate.id}`}
                      />
                    </div>
                    {isEdited && (
                      <Button
                        size="sm"
                        onClick={() => handleSave(rate.id)}
                        disabled={saving[rate.id]}
                        data-testid={`rate-save-${rate.id}`}
                      >
                        {saving[rate.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                        Save
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
